/**
 * SkillsUSA Preview Modal
 * Shows questions grouped by question bank before import
 */

import React, { useState } from 'react';

interface ExtractedQuestion {
  question_type: 'multiple_choice' | 'multi_select' | 'true_false' | 'short_answer';
  prompt_text: string;
  points_possible: number;
  explanation?: string | null;
  category?: string | null;
  allow_partial_credit?: boolean;
  answer_options: {
    option_text: string;
    is_correct: boolean;
    sequence_order: number;
  }[];
  metadata?: any;
}

interface BankPreview {
  name: string;
  external_id: string;
  questions: ExtractedQuestion[];
  question_count: number;
}

interface SkillsUSAPreviewModalProps {
  banks: BankPreview[];
  totalBanks: number;
  totalQuestions: number;
  errors: string[];
  warnings: string[];
  onConfirm: (banks: BankPreview[]) => void;
  onCancel: () => void;
  isImporting?: boolean;
}

export const SkillsUSAPreviewModal: React.FC<SkillsUSAPreviewModalProps> = ({
  banks: initialBanks,
  totalBanks,
  totalQuestions,
  errors,
  warnings,
  onConfirm,
  onCancel,
  isImporting = false,
}) => {
  const [banks, setBanks] = useState<BankPreview[]>(initialBanks);
  const [expandedBanks, setExpandedBanks] = useState<Set<string>>(new Set());

  const toggleBank = (externalId: string) => {
    setExpandedBanks((prev) => {
      const next = new Set(prev);
      if (next.has(externalId)) {
        next.delete(externalId);
      } else {
        next.add(externalId);
      }
      return next;
    });
  };

  const removeBank = (externalId: string) => {
    setBanks((prev) => prev.filter((b) => b.external_id !== externalId));
  };

  const currentTotalQuestions = banks.reduce((sum, b) => sum + b.question_count, 0);

  const uniqueErrors = Array.from(new Set(errors));
  const uniqueWarnings = Array.from(new Set(warnings));

  const formatQuestionType = (type: string) =>
    type.replaceAll('_', ' ').replaceAll(/\b\w/g, (c) => c.toUpperCase());

  const bankPlural = banks.length !== 1 ? 's' : '';
  const importButtonLabel = isImporting
    ? 'Importing...'
    : `Import ${banks.length} Bank${bankPlural} (${currentTotalQuestions} Questions)`;

  return (
    <div
      className="modal-overlay"
      onClick={isImporting ? undefined : onCancel}
      style={{ zIndex: 1000 }}
    >
      <div
        className="modal-content"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '900px',
          maxHeight: '90vh',
          overflow: 'auto',
          opacity: isImporting ? 0.5 : 1,
        }}
      >
        <div className="modal-header">
          <h2>
            Preview SkillsUSA Import ({banks.length} bank{banks.length !== 1 ? 's' : ''},{' '}
            {currentTotalQuestions} question{currentTotalQuestions !== 1 ? 's' : ''})
          </h2>
          <button
            className="modal-close"
            onClick={onCancel}
            disabled={isImporting}
          >
            x
          </button>
        </div>

        <div className="modal-body">
          {/* Errors */}
          {uniqueErrors.length > 0 && (
            <div
              style={{
                marginBottom: '1rem',
                padding: '0.75rem',
                backgroundColor: '#ffebee',
                border: '1px solid #f44336',
                borderRadius: '4px',
              }}
            >
              <strong style={{ color: '#c62828' }}>Errors ({uniqueErrors.length}):</strong>
              <ul style={{ marginTop: '0.5rem', marginBottom: 0, paddingLeft: '1.5rem' }}>
                {uniqueErrors.map((error) => (
                  <li key={error} style={{ color: '#c62828', marginBottom: '0.25rem' }}>
                    {error}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Warnings */}
          {uniqueWarnings.length > 0 && (
            <div
              style={{
                marginBottom: '1rem',
                padding: '0.75rem',
                backgroundColor: '#fff3e0',
                border: '1px solid #ff9800',
                borderRadius: '4px',
              }}
            >
              <strong style={{ color: '#e65100' }}>Warnings ({uniqueWarnings.length}):</strong>
              <ul style={{ marginTop: '0.5rem', marginBottom: 0, paddingLeft: '1.5rem' }}>
                {uniqueWarnings.map((warning) => (
                  <li key={warning} style={{ color: '#e65100', marginBottom: '0.25rem' }}>
                    {warning}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {banks.length === 0 ? (
            <div className="empty-state">No question banks to preview.</div>
          ) : (
            banks.map((bank) => {
              const isExpanded = expandedBanks.has(bank.external_id);
              return (
                <div
                  key={bank.external_id}
                  style={{
                    marginBottom: '1rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                  }}
                >
                  {/* Bank header */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0.75rem 1rem',
                      backgroundColor: '#f8f9fa',
                      borderBottom: isExpanded ? '1px solid #ddd' : 'none',
                    }}
                  >
                    <button
                      type="button"
                      aria-expanded={isExpanded}
                      onClick={() => toggleBank(bank.external_id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        flex: 1,
                        border: 'none',
                        background: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        textAlign: 'left',
                        font: 'inherit',
                      }}
                    >
                      <span style={{ marginRight: '0.5rem' }}>{isExpanded ? '\u25BC' : '\u25B6'}</span>
                      <strong>{bank.name}</strong>
                      <span style={{ color: '#666', marginLeft: '0.75rem', fontSize: '0.9rem' }}>
                        {bank.question_count} question{bank.question_count !== 1 ? 's' : ''}
                      </span>
                    </button>
                    <button
                      onClick={() => removeBank(bank.external_id)}
                      className="btn btn-small btn-danger"
                      disabled={isImporting}
                    >
                      Remove
                    </button>
                  </div>

                  {/* Expanded question list */}
                  {isExpanded && (
                    <div style={{ padding: '0.75rem 1rem' }}>
                      {bank.questions.map((question, qIdx) => (
                        <div
                          key={qIdx}
                          style={{
                            marginBottom: '0.75rem',
                            paddingBottom: '0.75rem',
                            borderBottom:
                              qIdx < bank.questions.length - 1 ? '1px solid #eee' : 'none',
                          }}
                        >
                          <div style={{ marginBottom: '0.25rem' }}>
                            <span style={{ fontWeight: 'bold', marginRight: '0.5rem' }}>
                              Q{qIdx + 1}
                            </span>
                            <span style={{ color: '#666', fontSize: '0.85rem' }}>
                              {formatQuestionType(question.question_type)} &bull;{' '}
                              {question.points_possible} pt{question.points_possible !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <div style={{ marginBottom: '0.25rem' }}>{question.prompt_text}</div>
                          {question.answer_options.length > 0 && (
                            <ul
                              style={{
                                margin: '0.25rem 0 0',
                                paddingLeft: '1.5rem',
                                fontSize: '0.9rem',
                              }}
                            >
                              {question.answer_options.map((opt) => (
                                <li key={opt.sequence_order}>
                                  {opt.option_text}
                                  {opt.is_correct && (
                                    <span
                                      style={{
                                        marginLeft: '0.5rem',
                                        color: 'green',
                                        fontWeight: 'bold',
                                      }}
                                    >
                                      Correct
                                    </span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div
          className="modal-footer"
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.5rem',
            marginTop: '1.5rem',
          }}
        >
          <button onClick={onCancel} className="btn btn-secondary" disabled={isImporting}>
            Cancel
          </button>
          <button
            onClick={() => onConfirm(banks)}
            className="btn btn-primary"
            disabled={banks.length === 0 || isImporting}
          >
            {importButtonLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
